import React, { useState, useEffect } from 'react';
import { Mesocycle, Session } from '../types';
import { localStorageService } from '../services/localStorage';

interface MesocycleDetailProps {
  mesocycle: Mesocycle;
  onBack: () => void;
  onSelectSession: (session: Session, weekNumber: number) => void;
  onDelete: (id: string) => void;
}

export const MesocycleDetail: React.FC<MesocycleDetailProps> = ({
  mesocycle,
  onBack,
  onSelectSession,
  onDelete,
}) => {
  const [currentWeek, setCurrentWeek] = useState(1);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    const start = new Date(mesocycle.startDate);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const week = Math.min(Math.max(1, Math.ceil(diffDays / 7)), mesocycle.weeks.length);
    setCurrentWeek(week);
  }, [mesocycle]);

  const week = mesocycle.weeks?.[currentWeek - 1];
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const handleDelete = () => {
    onDelete(mesocycle.id);
    setShowDeleteDialog(false);
  };

  if (!week) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow-sm">
          <div className="px-4 py-3 flex justify-between items-center">
            <button
              onClick={onBack}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <button
              onClick={() => setShowDeleteDialog(true)}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              aria-label="Delete mesocycle"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
          <div className="px-4 pb-4">
            <h1 className="text-2xl font-bold text-gray-900">{mesocycle.name}</h1>
          </div>
        </div>
        <div className="px-4 py-4">
          <div className="text-center text-gray-600">No weeks configured for this mesocycle</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm">
        <div className="px-4 py-3 flex justify-between items-center">
          <button
            onClick={onBack}
            className="flex items-center text-blue-600 hover:text-blue-800"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <button
            onClick={() => setShowDeleteDialog(true)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            aria-label="Delete mesocycle"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
        <div className="px-4 pb-4">
          <h1 className="text-2xl font-bold text-gray-900">{mesocycle.name}</h1>
        </div>
      </div>

      <div className="px-4 py-4">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => setCurrentWeek(Math.max(1, currentWeek - 1))}
            disabled={currentWeek === 1}
            className={`p-2 rounded-lg ${
              currentWeek === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-50 shadow-sm'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="text-center">
            <h2 className="text-lg font-semibold text-gray-900">
              Week {currentWeek} of {mesocycle.weeks.length}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {week.sessions.length} training days
            </p>
          </div>

          <button
            onClick={() => setCurrentWeek(Math.min(mesocycle.weeks.length, currentWeek + 1))}
            disabled={currentWeek === mesocycle.weeks.length}
            className={`p-2 rounded-lg ${
              currentWeek === mesocycle.weeks.length
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-50 shadow-sm'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div className="space-y-3">
          {week.sessions.map((session) => {
            const isCompleted = localStorageService.completedSessions.isCompleted(
              mesocycle.id,
              currentWeek,
              session.dayOfWeek
            );

            return (
              <div
                key={session.dayOfWeek}
                onClick={() => onSelectSession(session, currentWeek)}
                className="bg-white rounded-lg shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-500 mr-3">
                      {daysOfWeek[session.dayOfWeek]}
                    </span>
                    <h3 className="text-lg font-semibold text-gray-900">{session.name}</h3>
                  </div>
                  {isCompleted && (
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded">
                      Completed
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  {session.exercises.length} exercises
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {session.exercises.slice(0, 3).map((exercise, index) => (
                    <span
                      key={index}
                      className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                    >
                      {exercise.name}
                    </span>
                  ))}
                  {session.exercises.length > 3 && (
                    <span className="text-xs text-gray-500">
                      +{session.exercises.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Delete Mesocycle</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{mesocycle.name}"? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteDialog(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};